/**
 * feat-284 — the floating EN / ES / Both control on `/quiz-review`.
 *
 * A viewport-fixed segmented mode control. Nothing else in this app has one;
 * `design/curriculum-review-languages-notes.md` §2 is the spec for it and every
 * class below comes from §1's token table. This file mints no colour.
 */

import React, { useEffect, useRef, useState } from 'react';
import { languageLabel } from '../../services/translationsAdminService';
import { LanguageMode, LanguageSelection } from './quizReviewLanguage';

/**
 * Every on-screen string in this feature, in one place.
 *
 * Not decoration: a literal-text grep for copy that is visibly on screen returns
 * a true zero when it is inlined in JSX, which has burned this repo before.
 * Tests assert against these constants, not against retyped strings.
 */
export const QUIZ_REVIEW_LANGUAGE_COPY = {
  /** `role="group"` label — chrome, English in every mode (spec §Page chrome). */
  groupLabel: 'Curriculum language',
  english: 'EN',
  both: 'Both',
  /** < 768 px: `Both` has no room, so the segment becomes a glyph. */
  bothGlyph: '⇄',
  bothAriaLabel: 'Both languages',
  keycap: 'L',
  keycapTitle: 'Press L to cycle language',
  menuHeading: 'Second language',
  /** Announced by the visually-hidden live region — otherwise `l` is silent to a
   *  screen reader. `{language}` is the English NAME, never the raw code. */
  announceEnglish: 'English',
  announceBoth: (language: string) => `English and ${language}`,
  /** Column legend above the first unit in Both mode. English chrome. */
  legendEnglish: 'English',
  /** The two markers. */
  untranslatedChip: 'EN',
  untranslatedTitle: (language: string) =>
    `No ${language} for this field yet — showing the English source.`,
  staleChip: 'Outdated',
  staleTitle: 'The English source changed after this translation was made.',
} as const;

const SEGMENT_BASE =
  'px-2.5 md:px-3 py-1.5 rounded-full text-xs md:text-sm transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40';

/**
 * Active segment: the coral WASH carries the selection, the ink is `text-ink`.
 *
 * NOT `text-coral`, which is what the nav's selected-tab branch uses. Measured,
 * `coral #D55847` on `coral-light #FBEAE7` is 3.391:1 — below the 4.5:1 floor
 * for 14 px semibold, which WCAG counts as normal text, not large. `ink` on the
 * same wash is 12.902:1. Fill + weight + darker ink is still an unambiguous
 * selected state. The nav's existing pair is pre-existing and out of scope —
 * do not "harmonise" this by copying it (coordinator ruling, slice brief §11 Q2).
 */
const SEGMENT_ACTIVE = 'bg-coral-light text-ink font-semibold';
const SEGMENT_INACTIVE = 'text-inkMuted font-medium hover:text-ink hover:bg-cream-dark';

interface Props {
  selection: LanguageSelection;
  /** The tenant's configured non-English languages, `en`-free and non-empty.
   *  The caller renders nothing at all when it is empty (§2, AC9). */
  languages: string[];
  onChange: (next: LanguageSelection) => void;
}

const QuizReviewLanguageControl: React.FC<Props> = ({ selection, languages, onChange }) => {
  const { mode, language } = selection;
  const multi = languages.length > 1;
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Click-outside to close, the same shape as IconPickerPopover — there is no
  // Radix in this app.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpen]);

  const select = (nextMode: LanguageMode) => {
    setMenuOpen(false);
    onChange({ mode: nextMode, language });
  };

  const altLabel = language ? language.toUpperCase() : '';
  const altName = language ? languageLabel(language) : '';

  const announcement =
    mode === 'en'
      ? QUIZ_REVIEW_LANGUAGE_COPY.announceEnglish
      : mode === 'alt'
        ? altName
        : QUIZ_REVIEW_LANGUAGE_COPY.announceBoth(altName);

  return (
    <div
      ref={rootRef}
      data-testid="quiz-review-language-control"
      // z-40, NOT higher. ScorecardNavigation is `sticky top-0 z-50` and its
      // tenant dropdown and account menu are z-50 children; this pill must pass
      // UNDER them, never punch through an open menu.
      // feat-298: bottom-RIGHT at every width (was bottom-centre below md), so
      // the bottom-left Contents pill never overlaps it at 390 px (design Q2).
      className="fixed z-40 bottom-4 right-4 md:bottom-5 md:right-5"
    >
      {/* `multi` is part of the RENDER CONDITION rather than something an effect
          resets. There was a `useEffect(() => setMenuOpen(false), [languages,
          language])` here — "a language that vanished must not leave a menu
          hanging open" — and it was wrong in a way worth recording: an effect
          that unconditionally resets state ON MOUNT clobbers any interaction
          that lands before that first effect flushes. Under load the full jest
          run reproduced exactly that, roughly one run in three: click → toggle
          true → the still-pending MOUNT run of the effect → false, and the menu
          never appeared. Gating the render on `multi` covers the real case (a
          list that shrinks to one language stops rendering the menu) with no
          state to race. */}
      {menuOpen && multi && (
        // Opens UPWARD — the control is bottom-anchored.
        <div
          className="absolute bottom-full right-0 mb-2 w-[168px] bg-white rounded-2xl shadow-lg ring-1 ring-black/5 py-1.5"
          role="menu"
          aria-label={QUIZ_REVIEW_LANGUAGE_COPY.menuHeading}
        >
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-inkMuted">
            {QUIZ_REVIEW_LANGUAGE_COPY.menuHeading}
          </div>
          {languages.map((code) => (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={code === language}
              onClick={() => {
                setMenuOpen(false);
                // Picking a language from EN mode moves the reader into it;
                // picking one from Both keeps Both (spec §Assumptions 6 — Both
                // pairs English with the CURRENTLY selected language).
                onChange({ mode: mode === 'both' ? 'both' : 'alt', language: code });
              }}
              className={
                'w-full text-left px-3 py-1.5 text-sm flex items-baseline gap-2 ' +
                (code === language ? SEGMENT_ACTIVE : 'text-ink hover:bg-cream-dark')
              }
            >
              {/* English NAMES, not endonyms: page chrome does not translate. */}
              <span>{languageLabel(code)}</span>
              <span className="font-mono text-[11px] text-inkMuted">{code}</span>
            </button>
          ))}
        </div>
      )}

      <div
        role="group"
        aria-label={QUIZ_REVIEW_LANGUAGE_COPY.groupLabel}
        className="inline-flex items-center gap-1 rounded-full p-1 pr-2 bg-white/95 backdrop-blur-sm shadow-lg ring-1 ring-black/5"
      >
        <button
          type="button"
          aria-pressed={mode === 'en'}
          onClick={() => select('en')}
          className={`${SEGMENT_BASE} ${mode === 'en' ? SEGMENT_ACTIVE : SEGMENT_INACTIVE}`}
        >
          {QUIZ_REVIEW_LANGUAGE_COPY.english}
        </button>

        <button
          type="button"
          aria-pressed={mode === 'alt'}
          title={altName}
          onClick={() => {
            // With two or more languages, the segment doubles as the picker:
            // click it to enter that language, click it again to choose which.
            if (multi && mode === 'alt') setMenuOpen((o) => !o);
            else select('alt');
          }}
          className={`${SEGMENT_BASE} ${mode === 'alt' ? SEGMENT_ACTIVE : SEGMENT_INACTIVE}`}
        >
          {altLabel}
          {multi && <span className="ml-1 text-[9px] opacity-70">▾</span>}
        </button>

        <button
          type="button"
          aria-pressed={mode === 'both'}
          aria-label={QUIZ_REVIEW_LANGUAGE_COPY.bothAriaLabel}
          title={QUIZ_REVIEW_LANGUAGE_COPY.both}
          onClick={() => select('both')}
          className={`${SEGMENT_BASE} ${mode === 'both' ? SEGMENT_ACTIVE : SEGMENT_INACTIVE}`}
        >
          <span className="md:hidden" aria-hidden="true">
            {QUIZ_REVIEW_LANGUAGE_COPY.bothGlyph}
          </span>
          <span className="hidden md:inline">{QUIZ_REVIEW_LANGUAGE_COPY.both}</span>
        </button>

        {/* An affordance, not a button — no click handler. Hidden below 768 px,
            where there is no keyboard to press it with. */}
        <span className="hidden md:inline-flex ml-1 pl-2 border-l border-softBorder">
          <kbd
            title={QUIZ_REVIEW_LANGUAGE_COPY.keycapTitle}
            className="text-[10px] font-semibold text-inkMuted bg-cream-dark border border-softBorder rounded px-1.5 py-0.5"
          >
            {QUIZ_REVIEW_LANGUAGE_COPY.keycap}
          </kbd>
        </span>
      </div>

      {/* Without this the `l` hotkey is completely silent to a screen reader. */}
      <div className="sr-only" aria-live="polite" data-testid="quiz-review-language-announcement">
        {announcement}
      </div>
    </div>
  );
};

export default QuizReviewLanguageControl;
